'use client'

import type {
  EventBusContextProps,
  EventBusProviderProps,
  EventPayload,
  PublishFnType,
} from '@maestro/core/event/types'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import { filter, finalize, map, type Observable, Subject } from 'rxjs'

import { createLogger } from '@maestro/core/logger'
import { EventType } from '@maestro/data-layer'

const log = createLogger('maestro:core:event-bus')

export const EventBusContext = createContext<EventBusContextProps>({
  publish: () => {},
  subscribeTo: () => undefined,
  isPageViewPublished: () => false,
})
const { Provider } = EventBusContext

export const useEventBusContext = () => {
  const context = useContext(EventBusContext)
  if (!context) {
    throw new Error('useEventBusContext must be used within EventBusProvider')
  }
  return context
}

export const EventBusProvider = ({ children }: EventBusProviderProps) => {
  const eventStreamRef = useRef(new Subject<EventPayload>())
  const subscriberListRef = useRef(new Set<string>())
  const pageViewPublishedRef = useRef(false)
  const eventQueueRef = useRef<EventPayload[]>([])

  const publish = useCallback<PublishFnType>((eventType, data) => {
    if (eventType === EventType.PAGE_VIEW) {
      pageViewPublishedRef.current = true

      eventStreamRef.current.next({
        type: eventType,
        data,
      })

      setTimeout(() => {
        eventQueueRef.current.forEach((event) => {
          eventStreamRef.current.next(event)
        })
        eventQueueRef.current = []
      }, 150)
      return
    }

    if (!pageViewPublishedRef.current) {
      eventQueueRef.current.push({ type: eventType, data })
      return
    }

    eventStreamRef.current.next({
      type: eventType,
      data,
    })
  }, [])

  const subscribeTo = useCallback(
    <T,>(eventType: string, source: string): Observable<T> | undefined => {
      if (subscriberListRef.current.has(source)) {
        log.warn(
          `[EventBus subscribeTo] Source: "${source}" is already subscribed to eventType "${eventType}". Change source name or ensure single subscription.`
        )
        return undefined
      }

      subscriberListRef.current.add(source)

      return eventStreamRef.current.pipe(
        filter((event) => event.type === eventType),
        map((event) => event.data as T),
        finalize(() => {
          // triggered on unsubscription
          subscriberListRef.current.delete(source)
        })
      )
    },
    []
  )

  const value = useMemo<EventBusContextProps>(
    () => ({
      publish,
      subscribeTo,
      isPageViewPublished: () => pageViewPublishedRef.current,
    }),
    [publish, subscribeTo]
  )

  return <Provider value={value}>{children}</Provider>
}
